import { useEffect, useState, useCallback } from 'react';
import { usePlatformStore } from '@/lib/store';
import { fetchB2CFeatureMatrix, updatePlanFeatures, bridgeKillSwitchToB2C, fetchPlanFeatureDefaults } from '@/lib/api';
import { FEATURE_META, APP_B2C_ONLY_KEYS } from '@/lib/constants';
import { B2C_PLAN_ORDER, B2C_PLAN_DISPLAY, B2C_FEATURE_CATEGORIES } from '@/lib/constants';
import { Search, ChevronDown, Loader2, Save, RotateCcw } from 'lucide-react';
import { cn } from '@/utils/helpers';
import type { Tenant, PlanName, FeatureConfig, TenantPlan, B2CFeatureMatrix, PlanFeatureUpdate } from '@/types';

// Only B2B feature_configs columns (exclude B2C-only app features like max_active_trips), sorted alphabetically by label
const FEATURE_KEYS = Object.keys(FEATURE_META).filter(
  k => !APP_B2C_ONLY_KEYS.has(k),
).sort((a, b) => (FEATURE_META[a]?.label || '').localeCompare(FEATURE_META[b]?.label || '')) as (keyof typeof FEATURE_META)[];

interface Props {
  defaultTenantId: string;
}

type CellKey = string; // `${planId}::${featureId}`
interface CellState {
  is_enabled: boolean;
  limit_value: number | null;
  ceiling_enabled: boolean | null;
  ceiling_limit: number | null;
}

export function TenantOverridesTab({ defaultTenantId }: Props) {
  const { tenants, loadTenants, updateFeatures } = usePlatformStore();
  const [search, setSearch] = useState('');
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [section, setSection] = useState<'platform' | 'b2c'>('platform');

  // Plan defaults fetched from DB (keyed by plan_id)
  const [planDefaultsMap, setPlanDefaultsMap] = useState<Record<string, Record<string, boolean>>>({});

  // B2C feature state per tenant
  const [b2cMatrix, setB2cMatrix] = useState<B2CFeatureMatrix | null>(null);
  const [b2cCells, setB2cCells] = useState<Record<CellKey, CellState>>({});
  const [b2cDirty, setB2cDirty] = useState(false);
  const [b2cLoading, setB2cLoading] = useState(false);
  const [b2cSaving, setB2cSaving] = useState(false);

  useEffect(() => { loadTenants(); }, [loadTenants]);

  // Eagerly fetch plan defaults for all tenants so override counts are accurate
  useEffect(() => {
    const planIds = new Set<string>();
    tenants.forEach(t => {
      const p = t.plan as TenantPlan | undefined;
      if (p?.id && !planDefaultsMap[p.id]) planIds.add(p.id);
    });
    planIds.forEach(pid => {
      fetchPlanFeatureDefaults(pid).then(rows => {
        const map: Record<string, boolean> = {};
        rows.forEach(r => map[r.feature_key] = r.is_enabled);
        setPlanDefaultsMap(prev => ({ ...prev, [pid]: map }));
      });
    });
  }, [tenants]);

  const loadB2CMatrix = useCallback(async (tenantId: string) => {
    setB2cLoading(true);
    try {
      const data = await fetchB2CFeatureMatrix(tenantId);
      setB2cMatrix(data);

      const c: Record<CellKey, CellState> = {};
      for (const pf of data.planFeatures) {
        c[`${pf.plan_id}::${pf.feature_id}`] = {
          is_enabled: pf.is_enabled,
          limit_value: pf.limit_value,
          ceiling_enabled: pf.ceiling_enabled,
          ceiling_limit: pf.ceiling_limit,
        };
      }
      setB2cCells(c);
      setB2cDirty(false);
    } catch (err) {
      console.error('Failed to load B2C matrix:', err);
    }
    setB2cLoading(false);
  }, []);

  const handleExpand = (tenantId: string) => {
    if (expandedId === tenantId) {
      setExpandedId(null);
      setB2cMatrix(null);
      setB2cCells({});
      setB2cDirty(false);
    } else {
      setExpandedId(tenantId);
      loadB2CMatrix(tenantId);
      // Fetch plan feature defaults for the tenant's plan
      const tenant = tenants.find(t => t.id === tenantId);
      const tenantPlan = tenant?.plan as TenantPlan | undefined;
      if (tenantPlan?.id && !planDefaultsMap[tenantPlan.id]) {
        fetchPlanFeatureDefaults(tenantPlan.id).then(rows => {
          const map: Record<string, boolean> = {};
          rows.forEach(r => map[r.feature_key] = r.is_enabled);
          setPlanDefaultsMap(prev => ({ ...prev, [tenantPlan.id]: map }));
        });
      }
    }
  };

  const updateB2cCell = (planId: string, featureId: string, partial: Partial<CellState>) => {
    const key = `${planId}::${featureId}`;
    setB2cCells(prev => ({
      ...prev,
      [key]: { ...(prev[key] || { is_enabled: false, limit_value: null, ceiling_enabled: null, ceiling_limit: null }), ...partial },
    }));
    setB2cDirty(true);
  };

  const handleB2cSave = async (tenantId: string) => {
    if (!b2cMatrix) return;
    setB2cSaving(true);
    try {
      const updates: PlanFeatureUpdate[] = [];
      for (const [key, cell] of Object.entries(b2cCells)) {
        const [plan_id, feature_id] = key.split('::');
        updates.push({ plan_id, feature_id, ...cell });
      }
      await updatePlanFeatures(tenantId, updates, 'super_admin');
      setB2cDirty(false);
      await loadB2CMatrix(tenantId);
    } catch (err) {
      console.error('Failed to save B2C overrides:', err);
    }
    setB2cSaving(false);
  };

  const handleResetToDefaults = async (tenantId: string) => {
    if (!b2cMatrix) return;
    // Reset to global defaults by reloading from the default tenant
    setB2cSaving(true);
    try {
      const globalData = await fetchB2CFeatureMatrix(defaultTenantId);
      const updates: PlanFeatureUpdate[] = [];
      for (const pf of globalData.planFeatures) {
        updates.push({
          plan_id: pf.plan_id,
          feature_id: pf.feature_id,
          is_enabled: pf.is_enabled,
          limit_value: pf.limit_value,
          ceiling_enabled: pf.ceiling_enabled,
          ceiling_limit: pf.ceiling_limit,
        });
      }
      await updatePlanFeatures(tenantId, updates, 'super_admin');
      await loadB2CMatrix(tenantId);
      setB2cDirty(false);
    } catch (err) {
      console.error('Reset to defaults failed:', err);
    }
    setB2cSaving(false);
  };

  const filtered = tenants.filter(t => {
    if (search && !t.name.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  return (
    <div className="space-y-4">
      <p className="text-sm text-surface-500">
        Per-tenant feature overrides. Platform kill switches control B2B features.
        B2C Feature Limits let you override plan-level limits and ceilings for specific tenants.
      </p>

      <div className="relative w-64">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-surface-500" />
        <input type="text" value={search} onChange={e => setSearch(e.target.value)}
          placeholder="Search tenants..." className="input-field pl-10 py-2" />
      </div>

      <div className="space-y-2">
        {filtered.map(tenant => {
          const plan = tenant.plan as TenantPlan | undefined;
          const planName = (plan?.name || 'free') as PlanName;
          const features = tenant.features as FeatureConfig | undefined;
          const planDefaults = (plan?.id ? planDefaultsMap[plan.id] : null) || {};
          const overrideCount = features ? countOverrides(features, planDefaults) : 0;
          const expanded = expandedId === tenant.id;

          return (
            <div key={tenant.id} className="glass-panel overflow-hidden">
              {/* Header row */}
              <button onClick={() => handleExpand(tenant.id)}
                className="w-full flex items-center gap-4 p-4 text-left hover:bg-surface-50 transition-colors">
                <div className="w-9 h-9 rounded-full bg-gradient-to-br from-slate-700 to-slate-900 flex items-center justify-center shrink-0 shadow-sm ring-1 ring-slate-500/30">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M15 21v-8a1 1 0 0 0-1-1h-4a1 1 0 0 0-1 1v8"/><path d="M3 10a2 2 0 0 1 .709-1.528l7-5.999a2 2 0 0 1 2.582 0l7 5.999A2 2 0 0 1 21 10v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/></svg>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-surface-900">{tenant.name}</div>
                  <div className="text-xs text-surface-500 capitalize">{planName} plan</div>
                </div>
                {overrideCount > 0 && (
                  <span className="px-2 py-0.5 rounded-full text-[10px] font-medium badge-amber border border-amber-200 dark:border-amber-500/20">
                    {overrideCount} override{overrideCount > 1 ? 's' : ''}
                  </span>
                )}
                <ChevronDown className={cn('w-4 h-4 text-surface-500 transition-transform', expanded && 'rotate-180')} />
              </button>

              {/* Expanded content */}
              {expanded && (
                <div className="border-t border-surface-200">
                  {/* Section tabs */}
                  <div className="flex gap-1 px-4 pt-3 border-b border-surface-100">
                    <button onClick={() => setSection('platform')}
                      className={cn('px-3 py-2 text-xs font-medium border-b-2 -mb-px transition-colors',
                        section === 'platform' ? 'text-brand-600 border-brand-500' : 'text-surface-500 border-transparent hover:text-surface-800')}>
                      Platform Kill Switches
                    </button>
                    <button onClick={() => setSection('b2c')}
                      className={cn('px-3 py-2 text-xs font-medium border-b-2 -mb-px transition-colors',
                        section === 'b2c' ? 'text-brand-600 border-brand-500' : 'text-surface-500 border-transparent hover:text-surface-800')}>
                      B2C Feature Limits
                    </button>
                  </div>

                  {/* Platform Kill Switches */}
                  {section === 'platform' && features && (
                    <div className="p-4">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-1">
                        {FEATURE_KEYS.map(key => {
                          const enabled = (features as any)[key] === true;
                          const inPlan = (planDefaults as any)[key] === true;
                          const isOverride = enabled !== inPlan;

                          return (
                            <div key={key} className={cn(
                              'flex items-center justify-between py-2 px-3 rounded-lg',
                              isOverride && 'bg-amber-500/5 border border-amber-500/10',
                            )}>
                              <div className="flex items-center gap-2 min-w-0">
                                <span className="text-sm text-surface-700">{FEATURE_META[key].label}</span>
                                {isOverride && (
                                  <span className="text-[9px] px-1 py-0.5 rounded badge-amber shrink-0">
                                    override
                                  </span>
                                )}
                              </div>
                              <button
                                onClick={async () => {
                                  const newValue = !enabled;
                                  try {
                                    await updateFeatures(tenant.id, { [key]: newValue });
                                    // Bridge to B2C: set ceiling_enabled so end users are affected
                                    bridgeKillSwitchToB2C(tenant.id, key, newValue).catch(() => {});
                                  } catch {}
                                }}
                                className={cn(
                                  'relative w-9 h-5 rounded-full transition-colors shrink-0',
                                  enabled ? 'bg-brand-500' : 'bg-surface-200',
                                )}
                              >
                                <span className={cn(
                                  'block w-4 h-4 rounded-full bg-white shadow transform transition-transform mt-0.5',
                                  enabled ? 'translate-x-4 ml-0.5' : 'translate-x-0.5',
                                )} />
                              </button>
                            </div>
                          );
                        })}
                      </div>
                      {overrideCount > 0 && (
                        <button
                          onClick={async () => {
                            const resets: Record<string, boolean> = {};
                            FEATURE_KEYS.forEach(k => { resets[k] = (planDefaults as any)[k]; });
                            try {
                              await updateFeatures(tenant.id, resets);
                              // Bridge all resets to B2C — restore ceilings to null
                              FEATURE_KEYS.forEach(k => {
                                bridgeKillSwitchToB2C(tenant.id, k, (planDefaults as any)[k]).catch(() => {});
                              });
                            } catch {}
                          }}
                          className="mt-3 text-xs text-amber-600 dark:text-amber-400 hover:text-amber-700 dark:hover:text-amber-300 transition-colors"
                        >
                          Reset all to plan defaults
                        </button>
                      )}
                    </div>
                  )}

                  {/* B2C Feature Limits */}
                  {section === 'b2c' && (
                    <div className="p-4">
                      {b2cLoading ? (
                        <div className="flex items-center justify-center py-12">
                          <Loader2 className="animate-spin text-brand-600 dark:text-brand-400" size={20} />
                        </div>
                      ) : b2cMatrix && b2cMatrix.features.length > 0 ? (
                        <div className="space-y-4">
                          <div className="flex items-center justify-between">
                            <p className="text-xs text-surface-500">
                              Tenant-specific feature limits. These override the global defaults for this tenant.
                            </p>
                            <div className="flex gap-2">
                              <button onClick={() => handleResetToDefaults(tenant.id)}
                                disabled={b2cSaving}
                                className="flex items-center gap-1 px-3 py-1.5 text-xs text-surface-500 hover:text-surface-700 border border-surface-200 rounded-lg transition-colors">
                                <RotateCcw size={12} /> Reset to Defaults
                              </button>
                              {b2cDirty && (
                                <button onClick={() => handleB2cSave(tenant.id)}
                                  disabled={b2cSaving}
                                  className="btn-primary flex items-center gap-1 px-3 py-1.5 text-xs">
                                  {b2cSaving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />}
                                  Save
                                </button>
                              )}
                            </div>
                          </div>

                          <TenantB2CGrid
                            matrix={b2cMatrix}
                            cells={b2cCells}
                            updateCell={updateB2cCell}
                          />
                        </div>
                      ) : (
                        <div className="text-center py-8 text-xs text-surface-500">
                          No B2C features found for this tenant.
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}

        {filtered.length === 0 && (
          <div className="text-center py-12 text-sm text-surface-500">
            {search ? 'No tenants match your search.' : 'No tenants on the platform yet.'}
          </div>
        )}
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════ */
/* B2C GRID (reused within each tenant)                                       */
/* ═══════════════════════════════════════════════════════════════════════════ */

function TenantB2CGrid({ matrix, cells, updateCell }: {
  matrix: B2CFeatureMatrix;
  cells: Record<CellKey, CellState>;
  updateCell: (planId: string, featureId: string, partial: Partial<CellState>) => void;
}) {
  const sortedPlans = [...matrix.plans].sort((a, b) => {
    const ai = B2C_PLAN_ORDER.indexOf(a.plan_key as typeof B2C_PLAN_ORDER[number]);
    const bi = B2C_PLAN_ORDER.indexOf(b.plan_key as typeof B2C_PLAN_ORDER[number]);
    return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
  });

  const categories = [...new Set(matrix.features.map(f => f.category))].sort((a, b) => {
    const order = B2C_FEATURE_CATEGORIES as readonly string[];
    return (order.indexOf(a) === -1 ? 99 : order.indexOf(a)) - (order.indexOf(b) === -1 ? 99 : order.indexOf(b));
  });

  return (
    <div className="space-y-3">
      {categories.map(cat => {
        const features = matrix.features.filter(f => f.category === cat).sort((a, b) => a.name.localeCompare(b.name));
        return (
          <div key={cat} className="border border-surface-200 rounded-lg overflow-hidden">
            <div className="px-4 py-2 bg-surface-50 border-b border-surface-200">
              <h4 className="text-xs font-semibold text-surface-700 capitalize">{cat}</h4>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-surface-100">
                    <th className="text-left text-[10px] font-medium text-surface-500 px-3 py-2 w-40">Feature</th>
                    {sortedPlans.map(plan => (
                      <th key={plan.id} className="text-center text-[10px] font-medium text-surface-500 px-2 py-2" style={{ minWidth: 100 }}>
                        <div className="flex items-center justify-center gap-1">
                          <div className="w-2 h-2 rounded-sm" style={{ backgroundColor: B2C_PLAN_DISPLAY[plan.plan_key]?.color || '#999' }} />
                          {B2C_PLAN_DISPLAY[plan.plan_key]?.name || plan.plan_key}
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {features.map(feature => {
                    const isCount = feature.limit_type === 'count' || feature.limit_type === 'count_per_trip';
                    return (
                      <tr key={feature.id} className="border-b border-surface-50 last:border-0">
                        <td className="px-3 py-2">
                          <div className="text-xs font-medium text-surface-700">{feature.name}</div>
                        </td>
                        {sortedPlans.map(plan => {
                          const key = `${plan.id}::${feature.id}`;
                          const cell = cells[key] || { is_enabled: false, limit_value: null, ceiling_enabled: null, ceiling_limit: null };
                          return (
                            <td key={plan.id} className="text-center px-2 py-2">
                              <div className="flex flex-col items-center gap-1">
                                <button
                                  onClick={() => updateCell(plan.id, feature.id, { is_enabled: !cell.is_enabled })}
                                  className={cn('relative w-8 h-4 rounded-full transition-colors shrink-0',
                                    cell.is_enabled ? 'bg-brand-500' : 'bg-surface-200')}
                                >
                                  <span className={cn('block w-3 h-3 rounded-full bg-white shadow transform transition-transform mt-0.5',
                                    cell.is_enabled ? 'translate-x-4 ml-0.5' : 'translate-x-0.5')} />
                                </button>
                                {isCount && cell.is_enabled && (
                                  <input type="number"
                                    value={cell.limit_value ?? ''}
                                    onChange={e => updateCell(plan.id, feature.id, {
                                      limit_value: e.target.value === '' ? null : parseInt(e.target.value, 10),
                                    })}
                                    placeholder="∞"
                                    className="w-14 text-center text-[10px] input-field py-0.5 px-1"
                                  />
                                )}
                              </div>
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        );
      })}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════ */
/* HELPERS                                                                    */
/* ═══════════════════════════════════════════════════════════════════════════ */

function countOverrides(features: FeatureConfig, defaults: Record<string, boolean>): number {
  let count = 0;
  FEATURE_KEYS.forEach(k => {
    if ((features as any)[k] !== (defaults as any)[k]) count++;
  });
  return count;
}
